import * as React from 'react';
import * as _ from 'lodash';
import { ILkdpiInfo } from '@nkr/models/index';
import { OverlayTrigger, Table } from 'react-bootstrap';
import PopoverTemplate from './popover-template';
import styled from 'styled-components';

const TableWrapper = styled(Table)`
    & > thead > tr > th,
    & > tbody > tr > td {
        padding: 2.5px 5px;
    }

    & > thead > tr > th:nth-child(1n+2),
    & > tbody > tr > td:nth-child(1n+2) {
        text-align: center;
    }

    & > thead > tr > th:nth-child(1),
    & > tbody > tr > td:nth-child(1) {
        background-color: #eee !important;
    }

    .label {
        font-size: 100%;
    }
`;

/**
 * donor and recipient are the structural subsets this component reads, not the full contracts, so
 * both IDonor / IRecipient and the slimmer pre-select donor satisfy them. Every existing caller
 * keeps compiling; the recipient pre-select grid can pass its own shape.
 */
interface IProps {
    donor: {
        /** IDonor types age as a string; the pre-select contract sends a number. */
        age: string | number,
        bmi: number,
        gender: string,
        hasHistoryOfSmoking: boolean,
        height: number,
        race: string,
        sbp: number,
        serumCreatinine: number,
        weight: number
    },
    recipient: { weight: number },
    info: ILkdpiInfo,
    placement?: string,
    color?: string
}

const LkdpiInfo = (props: IProps) => {
    const { donor, recipient, info } = props;
    const { lkdpiIndex, gfr, isRelated, hlaBMismatchCount, hlaDrMismatchCount } = info;

    const overlay = (
        <div style={{ height: 300, overflowY: 'scroll', paddingRight: 5 }}>
            <div style={{ marginBottom: 10 }}>
                <span className='label label-warning' style={{ fontSize: '90%' }}>Value</span>
                <span>&nbsp;highlighted in orange when an assumed value was used because the donor profile did not contain a value.</span>
            </div>

            <TableWrapper condensed bordered>
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Recipient</th>
                        <th>Donor</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Gender</td>
                        <td>&nbsp;</td>
                        <td>{donor.gender}</td>
                    </tr>
                    <tr>
                        <td>Height</td>
                        <td>&nbsp;</td>
                        <td>{donor.height} cm</td>
                    </tr>
                    <tr>
                        <td>Weight</td>
                        <td>{recipient.weight} kg</td>
                        <td>{donor.weight} kg</td>
                    </tr>
                    <tr>
                        <td>BMI</td>
                        <td>&nbsp;</td>
                        <td>{donor.bmi}</td>
                    </tr>
                    <tr>
                        <td>Age</td>
                        <td>&nbsp;</td>
                        <td>{donor.age}</td>
                    </tr>
                    <tr>
                        <td>African-American</td>
                        <td>&nbsp;</td>
                        <td>{donor.race == null ? <span className='label label-warning'>No</span> : (donor.race == 'Black' ? 'Yes' : 'No')}</td>
                    </tr>
                    <tr>
                        <td>Serum Creatinine</td>
                        <td>&nbsp;</td>
                        <td>{_.round(donor.serumCreatinine, 2)}</td>
                    </tr>
                    <tr>
                        <td>GFR</td>
                        <td>&nbsp;</td>
                        <td>{_.round(gfr, 2)}</td>
                    </tr>
                    <tr>
                        <td>Systolic BP</td>
                        <td>&nbsp;</td>
                        <td>{donor.sbp > 0 ? donor.sbp : <span className='label label-warning'>120</span>}</td>
                    </tr>
                    <tr>
                        <td>History of smoking?</td>
                        <td>&nbsp;</td>
                        <td>{donor.hasHistoryOfSmoking != null ? (donor.hasHistoryOfSmoking ? 'Yes' : 'No') : <span className='label label-warning'>No</span>}</td>
                    </tr>
                    <tr>
                        <td>Relative</td>
                        <td>&nbsp;</td>
                        <td>{isRelated ? 'Yes' : 'No'}</td>
                    </tr>
                    <tr>
                        <td>B Mismatches</td>
                        <td>&nbsp;</td>
                        <td>{hlaBMismatchCount}</td>
                    </tr>
                    <tr>
                        <td>DR Mismatches</td>
                        <td>&nbsp;</td>
                        <td>{hlaDrMismatchCount}</td>
                    </tr>
                </tbody>
            </TableWrapper>

            <p><a href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6114098/' target='_blank'>Research Paper</a></p>

            <h4>Equation:</h4>

            <p style={{ width: '100%' }}>LKDPI = −11.30 + 1.85 * [(age-50) if age &gt; 50] −0.381 * eGFR + 1.17 * BMI (+ 22.34 if African-American) (+ 14.33 if history of cigarette use) + 0.44*SBP (- 21.68 if donor and recipient both male) (+ 27.30 if ABO incompatible) (- 10.61 if unrelated) + 8.57 * (# HLA-B mismatches) + 8.26 * (# HLA-DR mismatches) −50.87 * [min(D/RWR, 0.9)]</p>
        </div>
    );

    return (
        <OverlayTrigger
            trigger='click'
            placement={props.placement != null ? props.placement : 'right'}
            rootClose
            overlay={
                <PopoverTemplate>
                    {overlay}
                </PopoverTemplate>
            }
        >
            <a style={{ color: props.color, cursor: 'default' }}>{lkdpiIndex}</a>
        </OverlayTrigger>
    );
}

export default LkdpiInfo;